import Vue from "vue";
import Vuex from "vuex";
import { fetchFormData } from "@/apis";
import { EventBus } from '@/EventBus'
import { reactiveUpdateObject } from '@/utils/object'
import { PermissionTableCSModule } from './modules/permissionTableCSModule'
import { ClaimRemittanceDSModule } from "./modules/claimRemittanceDSModule";

Vue.use(Vuex);

const getDefaultState = () => {
  return {
    auth: { user: {} },
    role: "",
    root: null,
    layout: "Auth",
    locale: null,
    app: null,
    forms: [],
    products: {},
    claims: {},
    riskPanels: {},
    formPanel: {},
    stateMachine: {},
    tabManagement: {
      tabs: [],
      activeTab: 0,
    },
    isLoading: false,
    isLoadingCustom: false,
    lookupCache: {},
    eventList: [],
    instance: "",
    formSteps: {},
    paymentFundsData: JSON.parse(sessionStorage.getItem('paymentFundsData') || 'null'),
  };
};

export default new Vuex.Store({
  state: getDefaultState(),
  getters: {
    resolveLookup: (state) => (lookup, id) =>
      id &&
      (lookup.id in state.lookupCache
        ? state.lookupCache[lookup.id][id]
        : undefined),
    tabs: (state) => state.tabManagement.tabs,
    getActiveTab: (state) =>
      state.tabManagement.tabs[state.tabManagement.activeTab],
    getDefById: (state) => (id) => {
      function getById(id, start) {
        return (
          (start.id === id && start) ||
          (Array.isArray(start) &&
            start.reduce((i, c) => i || getById(id, c), undefined)) ||
          ((start.id === "Root" || !start.id || id.startsWith(start.id)) && Object.keys(start)
            .filter((k) => typeof start[k] === "object")
            .reduce((i, k) => i || getById(id, start[k]), undefined))
        );
      }

      return getById(id, state.root) || getById(id, state.products)
    },
    getFormModel: (state) =>
      state.tabManagement.tabs[state.tabManagement.activeTab]?.model,
    getModalModel: (state) =>
      state.tabManagement.tabs[state.tabManagement.activeTab]?.modalForm?.model,
    getPageModel: (state) =>
      state.tabManagement.tabs[state.tabManagement.activeTab]?.pageForm?.model,
    getRiskModel: (state) => {
      const risk =
        state.tabManagement.tabs[state.tabManagement.activeTab]?.modalForm;
      if (risk[0]) {
        return risk[0].model;
      }
    },
    getClaimCodeModel: (state) => {
      const claim = state.tabManagement.tabs[state.tabManagement.activeTab]?.modalForm;
      if (claim[0]) {
        return claim[0].model;
      }
    },
    getCurrentForm: (state) => {
      const activeTab = state.tabManagement.tabs[state.tabManagement.activeTab];
      if (!activeTab) return {};
      const modal = JSON.parse(JSON.stringify(activeTab.modalForm))
        .reverse()
        .find((m) => m.show);
      return modal ?? activeTab;
    },
    getCurrentFormStep: (state) => {
      return state.formSteps
    }
  },
  mutations: {
    SET_PAYMENT_FUNDS_DATA: (state, payload) => {
      state.paymentFundsData = payload;
      sessionStorage.setItem('paymentFundsData', JSON.stringify(payload));
    },
    CLEAR_PAYMENT_FUNDS_DATA(state) {
      state.paymentFundsData = null;
      sessionStorage.removeItem('paymentFundsData');
    },
    SET_AUTH: (state, payload) => (state.auth = payload),
    SET_ROLE: (state, payload) => (state.role = payload),
    SET_ROOT: (state, payload) => (state.root = payload),
    SET_LOCALE: (state, payload) => (state.locale = payload),
    SET_FORMS: (state, payload) => (state.forms = payload),
    SET_APP: (state, payload) => (state.app = payload),
    SET_LAYOUT: (state, payload) => (state.layout = payload),
    SET_TAB: (state, payload, index = state.tabManagement.activeTab) =>
      (state.tabManagement.tabs[index] = payload),
    ADD_TAB: (state, payload, i) =>
      i
        ? state.tabManagement.tabs.splice(i, 0, payload)
        : state.tabManagement.tabs.push(payload),
    REPLACE_TAB: (state, { payload, index }) =>
      state.tabManagement.tabs.splice(index, 0, payload),
    DELETE_TAB: (state, payload) => state.tabManagement.tabs.splice(payload, 1),
    SET_ACTIVE_TAB: (state, payload) =>
      (state.tabManagement.activeTab = payload),
    SET_LOADING: (state, payload) => (state.isLoading = payload),
    SET_LOADING_CUSTOM: (state, payload) => (state.isLoadingCustom = payload),
    ADD_TO_LOOKUP_CACHE: (state, { lookup, entries }) => {
      if (!(lookup.id in state.lookupCache)) {
        state.lookupCache[lookup.id] = {};
      }

      const entriesMap = Array.isArray(entries)
        ? entries.reduce((o, i) => Object.assign(o, { [i["ID"]]: i }), {
          entries,
        })
        : entries;
      Object.assign(state.lookupCache[lookup.id], entriesMap);
    },
    SET_RISK_PANELS: (state, payload) =>
      (state.riskPanels[payload.id] = payload),
    SET_ClaimCode_PANELS: (state, payload) =>
      (state.formPanel[payload.id] = payload),
    RESET_STORE: (state) => {
      const user = JSON.parse(JSON.stringify(state.auth.user));
      Object.assign(state, getDefaultState());
      Object.assign(state.auth.user, user);
    },

    ADD_OR_REPLACE_FORM_STEP: (state, payload) => {
      const cloneFromSteps = Object.assign(state.formSteps, {})
      state.formSteps = { ...cloneFromSteps, ...payload }
    }

  },
  actions: {
    setPaymentFundsData({ commit }, data) {
      commit('SET_PAYMENT_FUNDS_DATA', data);
    },
    clearPaymentFundsData({ commit }) {
      commit('CLEAR_PAYMENT_FUNDS_DATA');
    },
    SHOW_MODAL: async (state, payload) =>
      new Promise(
        (resolve) => (payload.callback = (result) => resolve(result))
      ),
    SHOW_MESSAGE: async (state, payload) =>
      new Promise(
        (resolve) => (payload.callback = (result) => resolve(result))
      ),
    SHOW_CONFIRM_MESSAGE: async (state, payload) =>
      new Promise(
        (resolve) => (payload.callback = (result) => resolve(result))
      ),
    SHOW_PAGE: async (state, payload) =>
      new Promise(
        (resolve) => (payload.callback = (result) => resolve(result))
      ),
    DELETE_TAB: async ({ commit }, payload) =>
      new Promise((resolve) => {
        commit("DELETE_TAB", payload);
        resolve();
      }),
    SHOW_SNACKBAR: (state, payload) =>
      new Promise(
        (resolve) => (payload.callback = (result) => resolve(result))
      ),
    SHOW_APP_TAB: async (state, payload) =>
      new Promise(
        (resolve) => (payload.callback = (result) => resolve(result))
      ),
    ADD_TO_EVENT_LIST: ({ state }, payload) => {
      state.eventList.push(payload);
    },
    EVENT_EMITTER: async function ({ state }, payload) {
      const relatedEvents = state.eventList.filter(
        (e) => e.target === payload.target
      );
      await Promise.all(relatedEvents.map((e) => e.fn.call(payload.context)));
    },
    RESET_APP_STATE: ({ commit }) => {
      commit("RESET_STORE");
    },
    /**
     * @param {any} state
     * @param {{formID: string, formName:string, ID:number}} payload
     * @returns {{entity: object, formState:object}}
     */
    UPDATE_AND_RERENDER_FORM: async ({ state, commit }, payload) => {
      const { formId, formName, ID } = payload
      const entity = await fetchFormData(formName, ID, formId);

      if (state.tabManagement.tabs[state.tabManagement.activeTab].model.ID === entity.ID) {
        await reactiveUpdateObject(state.tabManagement.tabs[state.tabManagement.activeTab].model, entity)
      }

      EventBus.$emit(`${formId.split(".").pop()}-rerender`, entity);

    },
    /**
     * @param {any} state
     * @param {{formID: string, ID:number}} payload
     * @returns {void}
     */
    UPDATE_STATE_MACHINE(state, payload) {

      const { formId, ID } = payload
      if (formId && ID) {
        window.setImmediate(() => {
          EventBus.$emit('Update-Form-State-Machine', payload);
        })
      } else {
        console.error('something went wrong in store ->  UPDATE_STATE_MACHINE  ')
      }

    },

    REMOVE_FORM_STEP({ state, commit }, payload) {
      const stateToMap = new Map(Object.entries(state.formSteps))
      if (stateToMap.delete(payload)) {
        const mapToState = Object.fromEntries(stateToMap)
        commit('ADD_OR_REPLACE_FORM_STEP', mapToState)
      }
    }



  },
  modules: {
    permissiontablecsmodule: PermissionTableCSModule,
    claimremittancedsmodule: ClaimRemittanceDSModule
  },
});
